// @flow

import {
  COMMITTED_CAPACITY_ENABLED_BACKEND_NAME,
  DEFAULT_VALUE_FOR_DB_ROW_ID,
  IS_ROW_OPENED_FOR_EDITING,
  MAX_INPUT_VALUE_FOR_ANY_FIELD,
} from '@src/constants';

import { areAllValuesInGivenObjectNonNull, pickKeysFromObject } from '../../common-utils/utils';
import { isFirstDimArraySubSetOfSecond } from '../custom-discounts/mld-for-field/utils';

import { ESTIMATE_COMMITTED_CAPACITY } from './enums';

export const getUnusedMetricsListForEstimateCC = (
  existingConfigRows,
  rateCardVersion,
  isRenewal
) => {
  const metricsListBasedOnRCVersion = getAvailableMetricsForEstimateCC(rateCardVersion, isRenewal);
  const usedMetricNames = getUsedMetricsListForEstimateCC(existingConfigRows);
  return metricsListBasedOnRCVersion.filter((x) => !usedMetricNames.includes(x.name));
};

export const getUsedMetricsListForEstimateCC = (existingConfigRows) => {
  return existingConfigRows.map((x) => x.metricName);
};

export const getCompleteMetricsList = (onlyLiveMetrics = true) => {
  if (onlyLiveMetrics) {
    return ESTIMATE_COMMITTED_CAPACITY.COMPLETE_METRICS_LIST.filter((metric) => metric.isLive);
  }
  return ESTIMATE_COMMITTED_CAPACITY.COMPLETE_METRICS_LIST;
};

// Returns metrics that are applicable for the given rate card version.
// If a metric has no `applicableRateCards` restriction, it is available on all rate cards.
// If it does have `applicableRateCards`, it is only available when the current RC is in that list.
// e.g., Connect CC is only available on the Tiered Pricing RC, while eCKU/CFU are available on all RCs.
export const getAvailableMetricsForEstimateCC = (rateCardVersion, isRenewal) => {
  const allMetrics = getCompleteMetricsList();

  return allMetrics.filter((metric) => {
    if (metric.hideForRenewals && isRenewal) {
      return false;
    }

    const hasNoRateCardRestriction = !metric.applicableRateCards;
    const isApplicableForCurrentRC = metric.applicableRateCards?.includes(rateCardVersion);

    return hasNoRateCardRestriction || isApplicableForCurrentRC;
  });
};

export const isGivenMetricApplicableForEstimateCC = (metricName, rateCardVersion, isRenewal) => {
  if (!metricName) {
    return false;
  }

  return getAvailableMetricsForEstimateCC(rateCardVersion, isRenewal).some(
    (metric) => metric.name === metricName
  );
};

export const getMetricConfigBasedOnMetricNameForEstimateCC = (metricName) => {
  return getCompleteMetricsList().find((metric) => metric.name === metricName);
};

export const getMutuallyExclusiveGroupForMetricForEstimateCC = (metricName) => {
  return getMetricConfigBasedOnMetricNameForEstimateCC(metricName)?.mutuallyExclusiveGroup;
};

export const getUsedMutuallyExclusiveGroupsForEstimateCC = (usedMetricsList) => {
  return usedMetricsList
    .map((name) => getMutuallyExclusiveGroupForMetricForEstimateCC(name))
    .filter(Boolean);
};

// Note: When mutuallyExclusiveGroup is not configured, it means that that metric can be added freely - e.g., ECKU
export const getIfGivenMetricShouldBeDisabledForEstimateCC = (
  metricConfig,
  usedMetricsList,
  usedMutuallyExclusiveGroups,
  hasActiveCCStarterCommit
) => {
  const isBlockedByCCStarterCommit =
    hasActiveCCStarterCommit && Boolean(metricConfig.disabledWhenCCStarterCommitActive);
  const isAlreadyUsed = usedMetricsList.includes(metricConfig.name);
  const mutuallyExclusiveGroup = metricConfig.mutuallyExclusiveGroup;
  const hasExclusiveGroup = Boolean(mutuallyExclusiveGroup);
  const belongsToDifferentGroupThanUsed =
    usedMutuallyExclusiveGroups.length > 0 &&
    !usedMutuallyExclusiveGroups.includes(mutuallyExclusiveGroup);
  const isBlockedByMutualExclusion = hasExclusiveGroup && belongsToDifferentGroupThanUsed;
  return isAlreadyUsed || isBlockedByMutualExclusion || isBlockedByCCStarterCommit;
};

export const canAddMultipleRowsForMetricForEstimateCC = (metricName) => {
  return getMetricConfigBasedOnMetricNameForEstimateCC(metricName)?.canAddMultipleRows !== false;
};

export const getMetricLabelFromMetricNameForEstimateCC = (metricName) => {
  const metricConfig = getMetricConfigBasedOnMetricNameForEstimateCC(metricName);
  return metricConfig?.label;
};

export const getMetricDimensionsFromMetricNameForEstimateCC = (metricName) => {
  const metricConfig = getMetricConfigBasedOnMetricNameForEstimateCC(metricName);
  return metricConfig?.dimensions ?? [];
};

export const getAreDimensionsIncompleteForEstimateCC = (dimensionRow, metricName) => {
  const dimensionKeys = getMetricDimensionsFromMetricNameForEstimateCC(metricName);
  return !areAllValuesInGivenObjectNonNull(pickKeysFromObject(dimensionRow, dimensionKeys));
};

export const getMetricDimensionsLabelsFromMetricNameForEstimateCC = (metricName) => {
  const metricConfig = getMetricConfigBasedOnMetricNameForEstimateCC(metricName);
  return metricConfig?.dimensionLabels ?? [];
};

export const getNonDimensionFieldsFromMetricNameForEstimateCC = (metricName) => {
  const metricConfig = getMetricConfigBasedOnMetricNameForEstimateCC(metricName);
  return metricConfig?.nonDimensionFields ?? [];
};

export const getAdminOnlyDimensionFieldsFromMetricNameForEstimateCC = (metricName) => {
  const metricConfig = getMetricConfigBasedOnMetricNameForEstimateCC(metricName);
  return metricConfig?.adminOnlyDimensionFields ?? [];
};

export const getAllDimAndNonDimConfigFieldsForEstimateCC = (metricName) => {
  return [
    ...getMetricDimensionsFromMetricNameForEstimateCC(metricName),
    ...getNonDimensionFieldsFromMetricNameForEstimateCC(metricName),
  ];
};

export const getNonDimensionFieldLabelsFromMetricNameForEstimateCC = (metricName) => {
  const metricConfig = getMetricConfigBasedOnMetricNameForEstimateCC(metricName);
  return metricConfig?.nonDimensionFieldLabels ?? [];
};

export const getConfiguredMetricRowsFromFormikValuesForEstimateCC = (formikValues) => {
  return formikValues?.[ESTIMATE_COMMITTED_CAPACITY.METRICS_LEVEL_CONFIGURATION_ARRAY] ?? [];
};

export const getDimensionLevelConfigArrayFromMetricRowForEstimateCC = (metricRow) =>
  metricRow?.[ESTIMATE_COMMITTED_CAPACITY.METRICS_DIMENSIONS_LEVEL_CONFIGURATION_ARRAY] ?? [];

export const getIfAnyRowIsBeingEditedAnyWhereInAnyMetricForEstimateCC = (configuredMetricRows) => {
  return configuredMetricRows.some((metric) =>
    getDimensionLevelConfigArrayFromMetricRowForEstimateCC(metric).some((row) =>
      getIfGivenRowIsBeingEditedORANewOneForEstimateCC(row)
    )
  );
};

export const getRowIdOfGivenRowForEstimateCC = (row) =>
  row?.[ESTIMATE_COMMITTED_CAPACITY.ID_BACKEND_FIELD_NAME];

export const getIfCCRowIsAdminEdited = (row) => {
  return row?.[ESTIMATE_COMMITTED_CAPACITY.IS_ADMIN_EDITED_BACKEND_FIELD_NAME] === true;
};

export const getIfGivenRowIsANewlyAddedRowForEstimateCC = (givenRow) => {
  return getRowIdOfGivenRowForEstimateCC(givenRow) === DEFAULT_VALUE_FOR_DB_ROW_ID;
};

export const getIfGivenRowIsBeingEditedORANewOneForEstimateCC = (row) => {
  return getIfGivenRowIsANewlyAddedRowForEstimateCC(row) || row[IS_ROW_OPENED_FOR_EDITING] === true;
};

export const getIfAnyRowMetricNameIsNullForEstimateCC = (configuredMetricRows) =>
  configuredMetricRows.some((metricRow) => metricRow.metricName === null);

export const getNewDimensionsRowWithDefaultsForEstimateCC = (metricName) => {
  const { dimensionDefaults, dimensions, nonDimensionFields, nonDimensionFieldsDefaults } =
    getMetricConfigBasedOnMetricNameForEstimateCC(metricName);
  return Object.fromEntries([
    ...dimensions.map((dimension, i) => [dimension, dimensionDefaults[i]]),
    ...nonDimensionFields.map((field, i) => [field, nonDimensionFieldsDefaults[i]]),
    [ESTIMATE_COMMITTED_CAPACITY.ID_BACKEND_FIELD_NAME, DEFAULT_VALUE_FOR_DB_ROW_ID],
  ]);
};

export const getDimensionsPathForEstimateCC = (metricIndex, dimensionIndex) => {
  return `${ESTIMATE_COMMITTED_CAPACITY.METRICS_LEVEL_CONFIGURATION_ARRAY}[${metricIndex}]["${ESTIMATE_COMMITTED_CAPACITY.METRICS_DIMENSIONS_LEVEL_CONFIGURATION_ARRAY}"][${dimensionIndex}]`;
};

export const getDimensionFieldPathForEstimateCC = (metricIndex, dimensionIndex, fieldName) => {
  return `${getDimensionsPathForEstimateCC(metricIndex, dimensionIndex)}.${fieldName}`;
};

export const getMetricsPathForEstimateCC = (metricIndex) => {
  return `${ESTIMATE_COMMITTED_CAPACITY.METRICS_LEVEL_CONFIGURATION_ARRAY}[${metricIndex}]`;
};

export const getIfAnyOtherRowBesidesTheGivenRowIsBeingEditedForEstimateCC = (
  configuredMetricRows,
  givenRow
) => {
  return configuredMetricRows.some((metricRow) => {
    return getDimensionLevelConfigArrayFromMetricRowForEstimateCC(metricRow).find(
      (innerRow) =>
        getIfGivenRowIsBeingEditedORANewOneForEstimateCC(innerRow) &&
        getRowIdOfGivenRowForEstimateCC(innerRow) !== getRowIdOfGivenRowForEstimateCC(givenRow)
    );
  });
};

export const getMinQuantityForMetricAndUserTypeForEstimateCC = (metricName, isUserAdmin) => {
  const metricConfig = getMetricConfigBasedOnMetricNameForEstimateCC(metricName);
  const keyToCheck = isUserAdmin ? 'minQuantityForAdmins' : 'minQuantityForNonAdmins';
  return metricConfig?.[keyToCheck] ?? MAX_INPUT_VALUE_FOR_ANY_FIELD;
};

export const getMaxQuantityForMetricAndUserTypeForEstimateCC = (metricName, isUserAdmin) => {
  const metricConfig = getMetricConfigBasedOnMetricNameForEstimateCC(metricName);
  const keyToCheck = isUserAdmin ? 'maxQuantityForAdmins' : 'maxQuantityForNonAdmins';
  return metricConfig?.[keyToCheck] ?? MAX_INPUT_VALUE_FOR_ANY_FIELD;
};

export const getIfDiscountIsEditableForMetricAndUserTypeForEstimateCC = (
  metricName,
  isUserAdmin
) => {
  if (isUserAdmin) {
    return true;
  }
  const metricConfig = getMetricConfigBasedOnMetricNameForEstimateCC(metricName);
  return metricConfig?.isDiscountEditableByNonAdmins ?? true;
};

/**
 * Calculates the effective discount as a weighted average across tiers up to the committed quantity.
 *
 * @param {Array<{start: number, end: number|null, discount: number}>} tiers - Tier config sorted by start ascending
 * @param {number} quantity - Committed quantity
 * @returns {number} Effective discount percentage (0-100)
 *
 * Example: quantity=500, tiers=[{start:1,end:10,discount:0},{start:11,end:50,discount:20},...]
 * Each tier contributes: (units in tier) × (1 - discount/100) to the weighted sum.
 * ED = 1 - (weightedSum / quantity)
 */
export const calculateEffectiveDiscount = (tiers, quantity) => {
  if (quantity <= 0 || !tiers || tiers.length === 0) {
    return 0;
  }

  let remainingQuantity = quantity;
  let weightedSum = 0;

  for (const tier of tiers) {
    if (remainingQuantity <= 0) {
      break;
    }

    const tierStart = tier.start;
    const tierEnd = tier.end ?? Infinity;
    const tierSize = tierEnd - tierStart + 1;
    const unitsInTier = Math.min(remainingQuantity, tierSize);

    // Each unit contributes (1 - discount/100) to the weighted sum
    weightedSum += unitsInTier * (1 - tier.discount / 100);
    remainingQuantity -= unitsInTier;
  }

  // ED = 1 - (weightedSum / quantity), expressed as a percentage, rounded to one decimal place
  const effectiveDiscount = (1 - weightedSum / quantity) * 100;
  return Math.round(effectiveDiscount * 10) / 10;
};

/**
 * Builds the effective tier config by merging static tiers with MLD overrides and global discount.
 *
 * For each static tier:
 *   - If an MLD entry for ConnectNumTasksTiered matches the given provider + connectorType + tier key, use MLD discount_value
 *   - Otherwise, use max(static tier discount, global discount)
 *
 * MLD matching uses isFirstDimArraySubSetOfSecond with dimension order: [provider, tier, connector_sku]
 *
 * @param {Array<{key: string, start: number, end: number|null, discount: number}>} staticTiers
 * @param {number} globalDiscount
 * @param {Array} mldMetrics - MLD metrics array with dimensions for provider, tier, and connector_sku
 * @param {Object} row
 * @param {string} metricName
 * @returns {Array<{start: number, end: number|null, discount: number}>} Merged tiers
 */
export const buildEffectiveTierConfig = (
  staticTiers,
  globalDiscount,
  mldMetrics,
  row,
  metricName
) => {
  if (!staticTiers || staticTiers.length === 0) {
    throw new Error('Static tiers config is required for building effective tier config');
  }

  const metricConfig = getMetricConfigBasedOnMetricNameForEstimateCC(metricName);
  const { tieredMetricName, tieredMetricMLDDimsExtractorFunc } = metricConfig;

  const filteredMldMetrics = (mldMetrics ?? []).filter(
    (metric) => metric.metric_name === tieredMetricName
  );

  return staticTiers.map((tier) => {
    const inputDims = tieredMetricMLDDimsExtractorFunc(row, tier.key);

    const mldMatchingRow = filteredMldMetrics.find((metricRowValues) => {
      // Note, we cant use getCurrentMLDRowDimensionValues as we are using the RAW metrics that the BE sends us!
      const metricRowDimValues = metricRowValues.dimensions.map((x) => x.dimension_name);
      return isFirstDimArraySubSetOfSecond(inputDims, metricRowDimValues);
    });

    const discount = mldMatchingRow
      ? mldMatchingRow.discount_value
      : Math.max(tier.discount, globalDiscount);

    return {
      start: tier.start,
      end: tier.end,
      discount,
    };
  });
};

export const getEffectiveDiscountForCommittedCapacity = (
  staticTiers,
  globalDiscount,
  mldMetrics,
  row,
  metricName,
  quantity
) => {
  const effectiveTiers = buildEffectiveTierConfig(
    staticTiers,
    globalDiscount,
    mldMetrics,
    row,
    metricName
  );
  return calculateEffectiveDiscount(effectiveTiers, quantity);
};

export const getDiscountMinMaxRangeForEstimateCC = (metricName, args) => {
  const metricConfig = getMetricConfigBasedOnMetricNameForEstimateCC(metricName);
  return metricConfig?.discountMinMaxRangeExtractorFunc?.(args) ?? null;
};

export const getDiscountBoundsCheckForEstimateCC = (metricName, discountValue, estimate, row) => {
  const range = getDiscountMinMaxRangeForEstimateCC(metricName, {
    rateCard: estimate?.inputs?.rate_card,
    tieredConfig: estimate?.tiered_config,
    globalDiscount: estimate?.inputs?.global_discount ?? 0,
    mldMetrics: estimate?.metrics_discount?.metrics ?? [],
    row,
    quantity: row?.[ESTIMATE_COMMITTED_CAPACITY.QUANTITY_BACKEND_FIELD_NAME],
  });

  if (!range) {
    return { range: null, isOutOfBounds: false };
  }

  const { minDiscount, maxDiscount } = range;
  const isOutOfBounds = discountValue < minDiscount || discountValue > maxDiscount;

  return { range, minDiscount, maxDiscount, isOutOfBounds };
};

export const getIfAnyCommittedCapacityLineItemsAreConfigured = (values) => {
  const isCCEnabled = values[COMMITTED_CAPACITY_ENABLED_BACKEND_NAME] === true;
  const configuredRows = getConfiguredMetricRowsFromFormikValuesForEstimateCC(values);
  return isCCEnabled && configuredRows.length > 0;
};
